import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  QrCode,
  Download,
  Print,
  Close,
  Share,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification } from '../../store/slices/uiSlice';
import { InventoryItem, QRCodeData } from '../../types/models';
import { QRCodeUtils } from '../../utils/qrCode';

const QRCodeDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  const { organization } = useSelector((state: RootState) => state.auth);
  
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [printableQrCodeUrl, setPrintableQrCodeUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const item = dialog.data as InventoryItem;
  const isOpen = dialog.open && dialog.type === 'qr-code';

  const generateQRCodes = useCallback(async () => {
    if (!item) return;

    setLoading(true);
    setError(null);

    try {
      const qrData: QRCodeData = {
        itemID: item.itemID,
        itemName: item.itemName,
        organizationID: organization?.id,
      };

      // Generate both regular and printable QR codes
      const [regularQR, printableQR] = await Promise.all([
        QRCodeUtils.generateQRCode(qrData),
        QRCodeUtils.generatePrintableQRCode(qrData),
      ]);

      setQrCodeUrl(regularQR);
      setPrintableQrCodeUrl(printableQR);
    } catch (error: any) {
      console.error('Error generating QR codes:', error);
      setError(error.message || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  }, [item, organization?.id]);

  useEffect(() => {
    if (isOpen && item) {
      generateQRCodes();
    }
  }, [isOpen, item, generateQRCodes]);

  const handleClose = () => {
    dispatch(closeDialog());
    setQrCodeUrl('');
    setPrintableQrCodeUrl('');
    setError(null);
  };

  const handleDownload = () => {
    if (!printableQrCodeUrl || !item) return;

    try {
      const filename = `qr-code-${item.itemName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      QRCodeUtils.downloadQRCode(printableQrCodeUrl, filename);
      
      dispatch(addNotification({
        message: 'QR code downloaded successfully',
        type: 'success',
      }));
    } catch (error: any) {
      dispatch(addNotification({
        message: 'Failed to download QR code',
        type: 'error',
      }));
    }
  };

  const handlePrint = () => {
    if (!printableQrCodeUrl || !item) return;

    try {
      QRCodeUtils.printQRCode(printableQrCodeUrl, item.itemName);
      
      dispatch(addNotification({
        message: 'QR code sent to printer',
        type: 'success',
      }));
    } catch (error: any) {
      dispatch(addNotification({
        message: 'Failed to print QR code',
        type: 'error',
      }));
    }
  };

  const handleShare = async () => {
    if (!qrCodeUrl || !item) return;

    try {
      if (navigator.share) {
        // Use Web Share API if available
        const response = await fetch(qrCodeUrl);
        const blob = await response.blob();
        const file = new File([blob], `${item.itemName}-qr-code.png`, { type: 'image/png' });

        await navigator.share({
          title: `QR Code for ${item.itemName}`,
          text: `QR code for inventory item: ${item.itemName}`,
          files: [file],
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.origin + '/item/' + item.itemID);
        dispatch(addNotification({
          message: 'Item link copied to clipboard',
          type: 'success',
        }));
      }
    } catch (error: any) {
      console.error('Error sharing QR code:', error);
      dispatch(addNotification({
        message: 'Failed to share QR code',
        type: 'error',
      }));
    }
  };

  if (!item) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <QrCode color="primary" />
            <Box>
              <Typography variant="h6">QR Code</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {item.itemName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : qrCodeUrl ? (
          <Box sx={{ textAlign: 'center' }}>
            {/* QR Code Image */}
            <Box
              sx={{
                display: 'inline-block',
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                boxShadow: 1,
                mb: 2,
              }}
            >
              <img
                src={qrCodeUrl}
                alt={`QR Code for ${item.itemName}`}
                style={{
                  width: '256px',
                  height: '256px',
                  display: 'block',
                }}
              />
            </Box>

            {/* Item Information */}
            <Typography variant="h6" gutterBottom>
              {item.itemName}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Item ID: {item.itemID}
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              <Tooltip title="Download QR Code">
                <IconButton onClick={handleDownload} color="primary">
                  <Download />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Print QR Code">
                <IconButton onClick={handlePrint} color="primary">
                  <Print />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Share QR Code">
                <IconButton onClick={handleShare} color="primary">
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Scan this QR code to quickly check in/out this item
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {qrCodeUrl && (
          <>
            <Button onClick={handleDownload} startIcon={<Download />}>
              Download
            </Button>
            <Button onClick={handlePrint} variant="contained" startIcon={<Print />}>
              Print
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeDialog;